import { Fragment, useContext } from 'react';
import { Modal, View, Image, Pressable, Platform, Text, ScrollView, TouchableOpacity } from 'react-native';
import { Icon as RNIcon } from 'react-native-elements';
import { WithLocalSvg } from 'react-native-svg';

import ShoppingBarSuccess from 'assets/images/shopping-bar-success.svg';
import { styles } from 'styles/components/suggestedProductsModal';
import { styles as productStyles } from 'styles/components/productItem';

import Product from 'types/models/Product';
import Venue from 'types/models/Venue';

import useSmallScreen from 'hooks/useSmallScreen';
import ProductType from 'types/enums/ProductType';
import ProductImageTypes from 'types/enums/ProductImageTypes';
import { greenColor } from 'styles/global';
import OrderContext from 'contexts/OrderContext';

const isWeb = Platform.OS === 'web';
const WrapperComponent = isWeb ? Fragment : View;
const wrapperProps = isWeb ? {} : { style: styles.productItemShadow };
interface GuarnicionesExtrasModalProps {
  currentVenue: Venue | null;
  visible: boolean;
  product: Product;
  onClose: () => void;
  items: Product[];
  onChangeQuantity: ((id: string, quantity: number, clothingSizeId?: number) => void) | undefined;
  onAddGuarnicionExtra?: (guarnicionExtra: Product, mainProduct: Product) => void;
}

export default function GuarnicionesExtrasModal({
  currentVenue,
  visible,
  product,
  onClose,
  items: guarnicionesExtras,
  onChangeQuantity,
  onAddGuarnicionExtra,
}: GuarnicionesExtrasModalProps) {
  const { currentShoppingCart, setCurrentShoppingCart } = useContext(OrderContext);
  const isSmallScreen = useSmallScreen();

  const handleListAdd = (guarnicionOrExtra: Product) => {
    if (!currentShoppingCart) return;
    const detailItem = currentShoppingCart.detail.find((item) => item.product.id === guarnicionOrExtra.id);
    if (detailItem) {
      onChangeQuantity?.(guarnicionOrExtra.id, detailItem.quantity + 1);
    } else {
      const updatedDetail = [...currentShoppingCart.detail, { product: guarnicionOrExtra, quantity: 1 }];
      setCurrentShoppingCart({ ...currentShoppingCart, detail: updatedDetail });
    }
  };
  const handleAddGuarnicionExtra = (guarnicionOrExtra: Product) => {
    if (onAddGuarnicionExtra) {
      onAddGuarnicionExtra(guarnicionOrExtra, product);
    } else {
      handleListAdd(guarnicionOrExtra);
    }

    onClose();
  };

  if (!guarnicionesExtras.length) return null;

  return (
    <Modal animationType="slide" transparent visible={visible} style={styles.modal}>
      <View style={styles.container}>
        <View
          style={[
            styles.responseDataContainer,
            !isWeb && isSmallScreen && { height: '60%' },
            isWeb && { height: '70%' },
            isWeb && isSmallScreen && { height: '75%' },
          ]}
        >
          <Pressable style={styles.closeButton} onPress={onClose}>
            <RNIcon name="close" style={styles.closeImage} />
          </Pressable>

          <View style={styles.modalImageContainer}>
            {isWeb ? (
              <Image source={ShoppingBarSuccess} style={[styles.modalImage, { width: 50, height: 62 }]} />
            ) : (
              <WithLocalSvg asset={ShoppingBarSuccess} style={[styles.modalImage, { width: 50, height: 62 }]} />
            )}
          </View>

          <View style={isWeb ? styles.content : styles.contentMobile}>
            <Text style={styles.title}>{product.typeId === ProductType.WGuarnicion ? 'GUARNICIONES' : 'EXTRAS'}</Text>

            <ScrollView
              style={[
                styles.generalTextContainer,
                isSmallScreen && !isWeb && styles.generalTextContainerMobile,
                isWeb && isSmallScreen && styles.generalTextContainerSmallWeb,
              ]}
              contentContainerStyle={{ alignItems: 'center', justifyContent: 'center' }}
            >
              {guarnicionesExtras.map((item) => (
                <WrapperComponent key={item.id + item.name} {...wrapperProps}>
                  <GuarnicionExtraProduct
                    product={item}
                    currentVenue={currentVenue}
                    onAdd={() => handleAddGuarnicionExtra(item)}
                  />
                </WrapperComponent>
              ))}
            </ScrollView>
          </View>

          <Pressable style={styles.goToOrdersContainer} onPress={onClose}>
            <Text style={styles.goToOrdersText}>Seguir con la compra</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

function GuarnicionExtraProduct({
  product,
  currentVenue,
  onAdd,
}: {
  product: Product;
  currentVenue: Venue | null;
  onAdd: () => void;
}) {
  const productImageUri =
    product.images.find((img) => img.productImageTypeId === ProductImageTypes.logo)?.path ||
    'https://via.placeholder.com/100';

  return (
    <TouchableOpacity onPress={onAdd} style={{ width: '100%' }}>
      <View style={productStyles.productItem}>
        <View style={productStyles.productImageContainer}>
          <Image style={productStyles.productImageNew} source={{ uri: productImageUri }} />
        </View>

        <View style={productStyles.productInfoContainer}>
          <View style={productStyles.productNameDescription}>
            <Text
              style={[productStyles.productName, { color: currentVenue?.appColor ?? greenColor }]}
              numberOfLines={2}
            >
              {product.name}
            </Text>
            <Text numberOfLines={1} style={{ marginTop: 5 }}>
              {product.description}
            </Text>
          </View>

          <View style={productStyles.priceQuantityContainer}>
            <Text style={[productStyles.priceText, { color: currentVenue?.appColor ?? greenColor }]}>
              {`$${Math.floor(product.price)}`}
            </Text>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
}
